import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import useInterval from 'utils/useInterval';
import { droneDeliveryDurationMinutes } from '../../../utils/date-utils';

interface DueInProps {
  expectedDeliveryDate: string;
  takeOffTimeMs?: number;
  id?: string;
}

const DueIn = (props: DueInProps) => {
  const [update, setUpdate] = useState(0);

  const oneMinInMs = 60000;
  const tenMinsInMs = 10 * oneMinInMs;
  const oneHourInMs = 60 * oneMinInMs;
  const droneDeliveryDurationMs = droneDeliveryDurationMinutes * 60 * 1000;
  const takeoffTime = new Date(props.expectedDeliveryDate).getTime() - droneDeliveryDurationMs;
  const currentTime = new Date().getTime();
  const timeDifference = takeoffTime - currentTime;
  const isDueSoon = timeDifference <= tenMinsInMs;
  const isOverDue = takeoffTime < currentTime;
  

  const hasTakeOffTimeMs = typeof props.takeOffTimeMs === 'number';
  // Add 10 seconds buffer to the takeoff time for landing pad countdown
  const landingPadTakeOffTimeMs = hasTakeOffTimeMs ? props.takeOffTimeMs + 10000 : undefined;

  // Calculate time difference for landing pad countdown
  const landingPadTimeDifference = landingPadTakeOffTimeMs !== undefined
    ? landingPadTakeOffTimeMs - currentTime
    : undefined;

  // Show landing pad countdown if we have the data (even if time has passed)
  const showLandingPadTimeCountDown = landingPadTimeDifference !== undefined;

  function getBgColour() {
    if (isOverDue === true) {
      return '#ED050514';
    } else if (!isOverDue && isDueSoon) {
      return '#FFEDE799';
    } else {
      return '#EAF9DE';
    }
  }

  function getColour() {
    if (isOverDue) {
      return '#ED0505';
    } else if (!isOverDue && isDueSoon) {
      return '#FC5000';
    } else {
      return '#008A00';
    }
  }

  function getDueTime(diff: number) {
    if (diff > (oneHourInMs * 48)) {
      return 'in the future';
    } else if (diff > (oneHourInMs * 24) && diff < oneHourInMs * 48) {
      return 'tomorrow';
    } else if (diff > oneHourInMs) {
      return `in ${Math.floor(diff / oneMinInMs / 60)} hrs and ${Math.floor((diff / oneMinInMs) % 60)} mins`;
    }
    return `in ${getMinutes(diff)} mins`;
  }

  function getMinutes(diff: number) {
    return Math.floor(Math.abs(diff / oneMinInMs));
  }

  function getSeconds(diff: number) {
    return Math.floor(Math.abs((diff / 1000) % 60));
  }

  useInterval(function() {
    // setCurrentTime(currentTime => currentTime + 1000);
    setUpdate(update + 1);
  }, 1000);

  const getDisplayContent = () => {
  if (!isOverDue && !isDueSoon) return `Takeoff ${getDueTime(timeDifference)}`;
  if (!isOverDue && isDueSoon && timeDifference > 0)
    return `Takeoff in ${getMinutes(timeDifference)}:${getSeconds(timeDifference).toString().padStart(2, '0')}`;
  if (!isOverDue && timeDifference <= 0)
    return `Takeoff in ${Math.abs(getMinutes(timeDifference))}:${Math.abs(getSeconds(timeDifference)).toString().padStart(2, '0')}`;
    return `Overdue by ${Math.abs(getMinutes(timeDifference))}:${Math.abs(getSeconds(timeDifference)).toString().padStart(2, '0')}`;
  };

  const CountdownBox = ({
        bgColor,
        textColor,
        content,
        isBlinking = false,
        flex = 1
      }: {
  bgColor: string;
  textColor: string;
  content: string;
  isBlinking?: boolean;
  flex?: number;
  }) => (
  <Box
    pl={0.5}
    display={'flex'}
    alignItems="center"
    justifyContent={'space-between'}
    sx={{
    padding: '8px 8px 8px 12px',
    borderRadius: 1,
    bgcolor: bgColor,
    flex: flex
    }}>
  <Typography
    noWrap
    variant={'body1'}
    sx={{
    color: textColor,
    fontWeight: 500,
    fontSize: 12
    }}>
    {content}
  </Typography>
  <FiberManualRecordIcon
    className={isBlinking ? 'animate-blink' : ''}
    sx={{
    padding: '2px',
    margin: '4px',
    alignContent: 'center',
    justifyContent: 'right',
    fontSize: 'small',
    color: textColor
    }}
  />
  </Box>
  );

  const TakeoffBox = ({ flex = 1 }: { flex?: number }) => (
    <CountdownBox
      bgColor={getBgColour()}
      textColor={getColour()}
      content={getDisplayContent()}
      isBlinking={isOverDue}
      flex={flex}
    />
  );

  const CountdownToTakeoffAfterButtonPress = () => {
    // Check if the timer has expired
    const hasExpired = landingPadTimeDifference !== undefined && landingPadTimeDifference <= 0;
    
    if (hasExpired) {
      return null;
    }

    // Generate appropriate content based on timer state
    const getContent = () => {
      return `${getMinutes(landingPadTimeDifference)}:${getSeconds(landingPadTimeDifference).toString().padStart(2, '0')}`;
    };

    return (
      <CountdownBox
        bgColor={"#caebf6"}
        textColor={"#0585ed"}
        content={getContent()}
        isBlinking={true}
      />
    );
  };

  return (
  <Box
    sx={{
    display: 'flex',
    gap: 1,
    width: '100%',
    marginTop: '8px'
    }}
  >
  {showLandingPadTimeCountDown ? (
    <>
    <TakeoffBox />
    <CountdownToTakeoffAfterButtonPress />
    </>
  ) : (
    <TakeoffBox />
  )}
  </Box>
  );
};

export default DueIn;
